import React, { FC, FormHTMLAttributes } from 'react';
import { Field, FormRenderProps } from 'react-final-form';
import { useQuery } from '@redux-requests/react';
import classNames from 'classnames';

import { flattenObject, t } from 'utils';
import { AppsConstants } from '../store/constants';
import { Heading, Note, Button } from 'components/ui';
import { RadiobuttonWrapper } from 'components/forms';
// import AppPricingFormOnetime from './AppPricingFormOnetime';
import { Error, SuccessMessage, TimifyLoader } from 'components';
// import AppPricingFormMembership from './AppPricingFormMembership';
import AppPricingFormPayPerUsage from './AppPricingFormPayPerUsage';
import Subscribe from '../AppSettings/components/Subscribe/Subscribe';

import styles from './AppPricing.module.scss';
import { IAppCountryForm } from './AppPricing';

export interface IAppPlanForm {
  name: string;
  monthlyPrice: number;
  description: string;
  onetimeFee: boolean;
  activationFeePrice: number;
}

export interface IAppPricingForm
  extends Omit<FormHTMLAttributes<HTMLFormElement>, 'children'>,
    FormRenderProps<any, Partial<any>> {}

const AppPricingForm: FC<IAppPricingForm> = (props) => {
  const {
    form,
    values,
    errors,
    pristine,
    className,
    submitting,
    submitError,
    submitErrors,
    submitFailed,
    handleSubmit,
    submitSucceeded,
    modifiedSinceLastSubmit,
    dirtyFieldsSinceLastSubmit,
  } = props;
  const containerClassName = classNames(styles.container, className);
  const { data: countries } = useQuery({ type: AppsConstants.APP_COUNTRIES });
  // remove label since Dropdown component works this way. It will use translationKey field
  const countriesWithoutLabel = (countries ?? []).map(({ label, ...rest }: { label: string }) =>
    label ? { ...rest } : {}
  );
  const { status, pricingType, allowedCountries } = values;
  // const { status, pricingType, plans, allowedCountries } = values;
  // const emptyPlanPayload = useMemo(
  //   (): IAppPlanForm => ({
  //     name: '',
  //     monthlyPrice: 0,
  //     description: '',
  //     activationFeePrice: 0,
  //     onetimeFee: false,
  //     // allowedCountries: allowedCountries.map((country: any) => ({
  //     //   monthlyPrice: 0,
  //     //   description: '',
  //     //   activationPrice: 0,
  //     //   onetimeFee: false,
  //     // })),
  //   }),
  //   []
  // );
  // disable editing if app is not in draft status or app has no countries
  const isFormDisabled = status !== 'DRAFT' || allowedCountries.length === 0;
  const errorsFlattened = errors && flattenObject(errors);
  // const submitErrorsFlattened = submitErrors && flattenObject(submitErrors);
  const prefilledCountries = values?.allowedCountries?.filter(
    (country: IAppCountryForm) => +country.prefillCountryIndex > -1
  );
  // array of all fields of prefilled countries to be used in error checking
  const prefilledCountriesWithIndexes = prefilledCountries
    ?.map((country: IAppCountryForm) => {
      const index: number = values?.allowedCountries?.findIndex(
        (formCountry: IAppCountryForm) => formCountry.country === country.country
      );
      const formattedCountry = { [`allowedCountries[${index}].`]: country };
      const flattenedFormattedCountry = flattenObject(formattedCountry);
      return Object.keys(flattenedFormattedCountry);
    })
    .flat();
  // create a cross section of submission errors and validation errors in order to disable submit button if the fields with error haven't been modified
  const submitErrorFields = Object.keys(errorsFlattened ?? {}).reduce(
    (errors: any, errorField: string) => {
      // exclude prefilled countries
      if (prefilledCountriesWithIndexes.includes(errorField)) return errors;
      // if the error field is not modified after last submitting and/or there's a validation error, put it here
      if (!dirtyFieldsSinceLastSubmit?.[errorField]) return [...errors, errorField];
      return errors;
    },
    []
  );

  return (
    <form className={containerClassName} onSubmit={handleSubmit} noValidate>
      <div className={styles.content}>
        {submitting && <TimifyLoader overlay />}
        <section className={styles.section}>
          <Heading icon='money-check-alt'>{t('pricing.entity.title')}</Heading>
          <Field
            label='pricing.free.title'
            name='pricingType'
            value='free'
            type='radio'
            disabled={isFormDisabled}
            component={RadiobuttonWrapper}
          />
          <Note className='mt-1 mb-2'>{t('pricing.free.hint')}</Note>
          {/* <Field
            label='pricing.onetime.title'
            name='pricingType'
            value='onetime'
            type='radio'
            disabled={isFormDisabled}
            component={RadiobuttonWrapper}
          />
          <Note className='mt-1 mb-2'>{t('pricing.onetime.hint')}</Note>
          {pricingType === 'onetime' && (
            <AppPricingFormOnetime
              form={form}
              errors={errors}
              values={values}
              countries={countriesWithoutLabel}
              submitErrors={submitErrors}
              isFormDisabled={isFormDisabled}
              dirtyFieldsSinceLastSubmit={dirtyFieldsSinceLastSubmit}
            />
          )}
          <Field
            label='pricing.membership.title'
            name='pricingType'
            value='membership'
            onChangeAddOn={
              plans?.length > 0
                ? null
                : () => {
                    // add empty plan
                    form?.change('plans', [emptyPlanPayload]);
                  }
            }
            type='radio'
            disabled={isFormDisabled}
            component={RadiobuttonWrapper}
          />
          <Note className='mt-1 mb-2'>{t('pricing.membership.hint')}</Note>
          {pricingType === 'membership' && (
            <AppPricingFormMembership
              form={form}
              errors={errors}
              values={values}
              countries={countriesWithoutLabel}
              submitErrors={submitErrors}
              isFormDisabled={isFormDisabled}
              dirtyFieldsSinceLastSubmit={dirtyFieldsSinceLastSubmit}
            />
          )} */}
          <Field
            label='pricing.payPerUsage.title'
            name='pricingType'
            value='payPerUsage'
            type='radio'
            disabled={isFormDisabled}
            component={RadiobuttonWrapper}
          />
          <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.hint')}</Note>
          {pricingType === 'payPerUsage' && (
            <AppPricingFormPayPerUsage
              form={form}
              errors={errors}
              values={values}
              countries={countriesWithoutLabel}
              submitErrors={submitErrors}
              isFormDisabled={isFormDisabled}
              dirtyFieldsSinceLastSubmit={dirtyFieldsSinceLastSubmit}
            />
          )}
        </section>
        {!modifiedSinceLastSubmit && (
          <SuccessMessage
            className={classNames(styles['success-message'], 'mt-4', 'mb-0')}
            isSuccess={submitSucceeded}
          >
            {t('global.changesSaved')}
          </SuccessMessage>
        )}
        {submitError && (
          <Error
            apiError={submitError}
            defaultError={submitError}
            isError={submitError && !modifiedSinceLastSubmit}
            className='mt-4'
          />
        )}
        {!isFormDisabled && (
          <section className={styles.section}>
            <Button
              disabled={
                submitting ||
                pristine ||
                (submitFailed && !modifiedSinceLastSubmit) ||
                Object.keys(submitErrorFields ?? {}).length > 0 ||
                Object.keys(errors ?? {}).length > 0
              }
              inline
              onClick={handleSubmit}
              type='button'
            >
              {t('save.changes')}
            </Button>
          </section>
        )}
      </div>
      <div className={styles.aside}>
        <Subscribe status={status} />
      </div>
    </form>
  );
};

export default AppPricingForm;
